import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#0f172a', // slate-900
  padding: '80px 24px',
  overflow: 'hidden',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    padding: '128px 48px'
  },
  [theme.breakpoints.up('lg')]: {
    padding: '128px 64px'
  }
}));

const ContentWrapper = styled(Box)({
  width: '100%',
  margin: '0 auto'
});

const HeaderWrapper = styled(Box)(({ theme }) => ({
  maxWidth: '768px',
  margin: '0 auto',
  textAlign: 'center',
  marginBottom: '64px',
  opacity: 0,
  transform: 'translateY(20px)',
  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
  '&.is-visible': {
    opacity: 1,
    transform: 'translateY(0)'
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '96px'
  }
}));

const Title = styled('h2')(({ theme }) => ({
  fontSize: '36px',
  fontWeight: 800,
  lineHeight: 1.2,
  letterSpacing: '-0.025em',
  color: '#ffffff',
  margin: 0,
  marginBottom: '16px',
  [theme.breakpoints.up('md')]: {
    fontSize: '48px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '60px'
  }
}));

const GradientText = styled('span')({
  background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  display: 'inline'
});

const Subtitle = styled('p')(({ theme }) => ({
  marginTop: '16px',
  fontSize: '18px',
  color: '#94a3b8', // slate-400
  margin: '0 auto',
  lineHeight: 1.6,
  fontWeight: 500,
  [theme.breakpoints.up('md')]: {
    fontSize: '20px'
  }
}));

const StepsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '32px',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px'
  }
}));

const StepCard = styled(Box)(({ theme, delay }) => ({
  position: 'relative',
  backgroundColor: '#1e293b', // slate-800
  border: '1px solid #334155', // slate-700
  borderRadius: '12px',
  padding: '32px',
  transition: 'border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease, opacity 0.6s ease-out',
  opacity: 0,
  transform: 'translateY(20px)',
  transitionDelay: delay || '0ms',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  '&.is-visible': {
    opacity: 1,
    transform: 'translateY(0)'
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: '#22d3ee', // cyan-400
    boxShadow: '0 0 25px rgba(34, 211, 238, 0.2)'
  },
  [theme.breakpoints.down('md')]: {
    padding: '24px'
  }
}));

const ImageContainer = styled(Box)({
  width: '100%',
  height: '200px',
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  borderRadius: '8px'
});

const StepImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  animation: 'slideInFromRight 1.5s ease-out',
  '@keyframes slideInFromRight': {
    '0%': {
      transform: 'translateX(100%)',
      opacity: 0
    },
    '100%': {
      transform: 'translateX(0)',
      opacity: 1
    }
  }
});

const StepNumber = styled('div')({
  position: 'absolute',
  top: '16px',
  right: '16px',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #22d3ee, #a5b4fc)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 700,
  color: '#0f172a'
});

const StepTitle = styled('h3')({
  fontSize: '20px',
  fontWeight: 700,
  color: '#ffffff',
  marginBottom: '12px',
  lineHeight: 1.3
});

const StepDescription = styled('p')({
  color: '#94a3b8', // slate-400
  fontSize: '14px',
  lineHeight: 1.6,
  margin: 0
});

const HowItWorksSection = () => {
  const headerRef = useRef(null);
  const cardsRef = useRef([]);

  const steps = [
    {
      image: '/images/diagnose.gif',
      title: 'Diagnose',
      description: 'Find what\'s holding your local visibility back - from missing NAP details to weak reviews and unoptimized posts - with instant fix suggestions.',
      delay: '100ms'
    },
    {
      image: '/images/benchmarking.gif',
      title: 'Benchmarking',
      description: 'Compare your Visibeen Score and map rankings with top competitors to see exactly where you stand and what drives their visibility.',
      delay: '200ms'
    },
    {
      image: '/images/task.gif',
      title: 'Tasks',
      description: 'Get AI-generated, impact-based tasks with clear actions, templates, and score improvement estimates to boost your rankings faster.',
      delay: '300ms'
    },
    {
      image: '/images/execution.gif',
      title: 'Execution',
      description: 'Turn insights into action - fix issues, track improvements, and watch your Visibeen Score and local visibility rise in real time.',
      delay: '400ms'
    }
  ];

  // Scroll animations
  useEffect(() => {
    const observerOptions = {
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    cardsRef.current.forEach(card => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <SectionContainer>
      <ContentWrapper>
        {/* Section Header */}
        <HeaderWrapper ref={headerRef} className="animate-on-scroll">
          <Title>
            How Visibeen <GradientText>Works</GradientText>
          </Title>
          <Subtitle>
            Your Local SEO Co-Pilot
          </Subtitle>
        </HeaderWrapper>

        {/* Steps Grid */}
        <StepsGrid>
          {steps.map((step, index) => (
            <StepCard 
              key={index}
              delay={step.delay}
              ref={(el) => (cardsRef.current[index] = el)}
              className="animate-on-scroll"
            >
              <StepNumber>{index + 1}</StepNumber>
              <ImageContainer>
                <StepImage
                  src={step.image}
                  alt={step.title}
                />
              </ImageContainer>
              <StepTitle>{step.title}</StepTitle>
              <StepDescription>{step.description}</StepDescription>
            </StepCard>
          ))}
        </StepsGrid>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default HowItWorksSection;
